/**
 * @module components/Form/FormHazard
 * @author Charles Blais <charles.blais@canada.ca>
 *
 * Hazard form build a URL that is used to query the hazard tool.
 * Parameters of the hazard tool are:
 *  - year
 *  - site designation
 *  - coordinates
 */

import { useMemo, useState } from 'react';
import { useFormik } from 'formik';

import * as Yup from 'yup';

import { Form, Col, Button, Alert } from 'react-bootstrap';

import { useLanguageState } from '../../contexts/language-state-provider';

import { useAppState } from '../../contexts/app-state-provider';

import FormGroupModels from './form-group-models';

import FormGroupSiteDesignation from './form-group-site-designation';

import FormGroupSiteDesignationXS from './form-group-site-designation-xs';

import FormGroupSiteDesignationXV from './form-group-site-designation-xv';

import FormGroupCoordinates from './form-group-coordinates';

import CoordinatesMap from './coordinates-map';


type Props = {
  onSubmit: (v: HazardFormInputs) => void,
  initialValues: HazardFormInputs
}


/**
 * React component
 * ===============
 */
const FormHazard = ({
  onSubmit,
  initialValues,
}: Props) => {
  const [lang] = useLanguageState();
  const [state] = useAppState();

  // Create the schema that will validate elements of the form
  // this object will only change if the contexts lang and state
  // change
  const HazardSchema = useMemo(() =>
    Yup.object().shape({
      code: Yup.string()
        .required(),
      siteDesignation: Yup.string()
        .oneOf(['XS', 'XV']).required(),
      siteDesignationXV: Yup.number()
        .when('siteDesignation', {
          is: (v) => v === 'XV',
          then: Yup.number()
            .min(state.form.siteDesignationsXVLimit[0], lang.form.error.siteDesignationXV)
            .max(state.form.siteDesignationsXVLimit[1], lang.form.error.siteDesignationXV)
            .required(lang.form.error.siteDesignationXV),
          otherwise: Yup.number()
            .notRequired()
        }),
      siteDesignationXS: Yup.string()
        .when('siteDesignation', {
          is: (v) => v === 'XS',
          then: Yup.string()
            .oneOf(state.form.siteDesignationsXSOptions, lang.form.error.siteDesignationXS)
            .required(lang.form.error.siteDesignationXS),
          otherwise: Yup.string()
            .notRequired()
        }),
      latitude: Yup.number()
        .min(state.form.latitudeLimits[0], lang.form.error.latitude)
        .max(state.form.latitudeLimits[1], lang.form.error.latitude)
        .required(lang.form.error.latitude),
      longitude: Yup.number()
        .min(state.form.longitudeLimits[0], lang.form.error.longitude)
        .max(state.form.longitudeLimits[1], lang.form.error.longitude)
        .required(lang.form.error.longitude),
    })
    , [state, lang]);


  // Create the form using the hook
  const formik = useFormik({
    initialValues,
    validationSchema: HazardSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit,
  });

  // Conditionally render the site designation details
  // information based on the selected site designation value
  let siteDetails = null;
  if (formik.values.siteDesignation === 'XS') {
    siteDetails = (
      <FormGroupSiteDesignationXS
        value={formik.values.siteDesignationXS}
        onChange={formik.handleChange}
      />
    )
  } else if (formik.values.siteDesignation === 'XV') {
    siteDetails = (
      <FormGroupSiteDesignationXV
        value={formik.values.siteDesignationXV}
        onChange={formik.handleChange}
      />
    )
  }

  const [address, setAddress] = useState('');

  // Creact callback that will set the latitude and longitude
  // [TODO] formik hook always get updated on each render
  const onCoordinates: FormOnCoordinates = (latitude, longitude) => {
    formik.setValues({
      ...formik.values,
      latitude: Number(Number(latitude).toFixed(3)),
      longitude: Number(Number(longitude).toFixed(3)),
    });
  };

  return (
    // @ts-ignore: ignore onSubmit oddity
    <Form horizontal onSubmit={formik.handleSubmit}>
      <FormGroupModels
        value={formik.values.code}
        onChange={formik.handleChange}
      />
      <FormGroupSiteDesignation
        value={formik.values.siteDesignation}
        code={formik.values.code}
        onChange={formik.handleChange}
      />
      {siteDetails}
      <FormGroupCoordinates
        values={[
          formik.values.latitude,
          formik.values.longitude
        ]}
        onCoordinates={onCoordinates}
        address={address}
        onAddress={setAddress}
      />
      {(formik.values.longitude !== undefined && formik.values.latitude !== undefined && formik.values.longitude !== 0 && formik.values.latitude !== 0) &&
        <Col smOffset={2} style={{ paddingTop: 10 }}>
          <p>{lang.form.mapConfirm}</p>
          <CoordinatesMap
            values={[
              formik.values.latitude,
              formik.values.longitude
            ]}
            onCoordinates={onCoordinates}
          />
          <p>{lang.form.selectedCoord} {Number(formik.values.latitude).toFixed(3)},{Number(formik.values.longitude).toFixed(3)}</p>
        </Col>
      }
      {Object.keys(formik.errors).length !== 0 &&
        <Col smOffset={2} style={{ padding: 10 }}>
          <Alert bsStyle="danger">
            <p>{lang.form.beforeSubmit}</p>
            <ul>
              {Object.values(formik.errors).map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </Alert>
        </Col>
      }
      <Col style={{ paddingTop: 10 }}>
        <Button
          type="submit"
          bsStyle="primary"
          style={{ width: '100%' }}
        >
          {lang.form.submit}
        </Button>
      </Col>
    </Form>
  )
};


export default FormHazard;